// Project-attribution modal for the Actual-to-Budget tab. Shared by the row
// pencil (ActualToBudgetView) and the drill-down drawer (CellDrilldownDrawer)
// so a SAP row can be attributed from either place.
//
// Two modes, switched on whether the row is a synthetic SAP row:
//   - SAP row (row.sap_transaction_id) → "Update SAP Record": attribute the
//     SAP posting to an EXISTING project (POST /sap-project/{id}/ {project_id}).
//     The manual attribution wins over reconciled derivation (design §4A); the
//     server validates the project exists and blocks split-batch members (B2).
//     NO budget line is created here.
//   - Budgeted row (row.budget_line_id) → "Rename…": set the display_alias
//     (display name only, NOT a join key) AND optionally the project_id (which
//     DOES drive A2B matching, design §4E) via PATCH /lines/{id}/alias/.
//
// Both modes attribute to EXISTING projects only — the picker is a typable,
// client-side-filtered combobox over GET /api/projects/; it stores the FK,
// never free text.

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Icon, Modal, styles } from '../reconciliation/primitives';
import { setSapProject, setLineAlias, fetchProjects } from './api';

// Typable, existing-projects-only project combobox. `value` is the selected
// project_id (or null); `onChange(projectId)` fires on pick. Filters client-
// side across project name + area as the user types. Grouped by area to match
// the budget-line picker's UX (ReviewQueueView.BudgetLineSelect).
export function ProjectPicker({ projects, value, onChange, placeholder = 'Search projects…', disabled }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const boxRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => {
      if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  useEffect(() => {
    if (open && inputRef.current) inputRef.current.focus();
  }, [open]);

  const selected = useMemo(
    () => projects.find((p) => p.id === value) || null,
    [projects, value]
  );

  const groups = useMemo(() => {
    const q = query.trim().toLowerCase();
    const filtered = q
      ? projects.filter(
          (p) =>
            p.name.toLowerCase().includes(q) ||
            (p.area || '').toLowerCase().includes(q)
        )
      : projects;
    const byArea = new Map();
    filtered.forEach((p) => {
      const area = p.area || 'No area';
      if (!byArea.has(area)) byArea.set(area, []);
      byArea.get(area).push(p);
    });
    return Array.from(byArea.entries());
  }, [projects, query]);

  return (
    <div className="bg-combobox" ref={boxRef}>
      <button
        type="button"
        className="a2b-project-trigger"
        disabled={disabled}
        onClick={() => {
          setOpen((o) => !o);
          setQuery('');
        }}
      >
        <span className={selected ? 'a2b-project-current' : 'a2b-project-placeholder'}>
          {selected ? selected.name : 'Choose a project…'}
        </span>
        <Icon d="M6 9l6 6 6-6" size={11} sw={2} />
      </button>
      {open && (
        <div className="bg-combobox-menu">
          <input
            ref={inputRef}
            className="bg-combobox-search"
            placeholder={placeholder}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
          {groups.length === 0 && <div className="bg-combobox-empty">No matching projects</div>}
          {groups.map(([area, ps]) => (
            <div key={area}>
              <div className="bg-combobox-group">{area}</div>
              {ps.map((p) => (
                <div
                  key={p.id}
                  className="bg-combobox-opt"
                  onMouseDown={() => {
                    setOpen(false);
                    onChange(p.id);
                  }}
                >
                  {p.name}
                </div>
              ))}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function AliasPopover({ row, onClose, onToast, onChanged }) {
  const isSap = Boolean(row.sap_transaction_id);
  // SAP mode → attribute to a project; budgeted mode → display_alias + project.
  const [projectId, setProjectId] = useState(row.project_id || null);
  const [displayName, setDisplayName] = useState(row.name || '');
  const [projects, setProjects] = useState([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    fetchProjects()
      .then((res) => {
        if (!cancelled) setProjects(res.data || []);
      })
      .catch(() => {
        if (!cancelled) setError('Could not load projects');
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const handleSave = async () => {
    setBusy(true);
    setError(null);
    try {
      if (isSap) {
        await setSapProject(row.sap_transaction_id, projectId || null);
        onToast({ text: 'SAP record updated', tone: 'good' });
      } else if (row.budget_line_id) {
        await setLineAlias(row.budget_line_id, {
          displayAlias: displayName.trim() || null,
          projectId: projectId || null,
        });
        onToast({ text: 'Budget line updated', tone: 'good' });
      }
      onChanged();
      onClose();
    } catch (err) {
      setError(err.response?.data?.detail || err.message || 'Save failed');
      setBusy(false);
    }
  };

  return (
    <Modal
      title={`Update “${row.name}”`}
      overflowVisible
      onClose={onClose}
      footer={
        <>
          <span style={{ marginLeft: 'auto' }} />
          <button style={styles.ghostBtn} onClick={onClose}>
            Cancel
          </button>
          <button style={styles.primaryBtn} disabled={busy} onClick={handleSave}>
            {busy ? 'Saving…' : 'Save'}
          </button>
        </>
      }
    >
      <div className="a2b-form">
        {!isSap && (
          <label className="a2b-field">
            Display name
            <input
              className="fc-text"
              value={displayName}
              placeholder={row.name || 'Display name'}
              onChange={(e) => setDisplayName(e.target.value)}
            />
          </label>
        )}
        <label className="a2b-field">
          Project
          <ProjectPicker
            projects={projects}
            value={projectId}
            onChange={setProjectId}
          />
        </label>
        {error && <div className="a2b-error">{error}</div>}
      </div>
    </Modal>
  );
}
